import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  enrichSecondSeniorApplyPayload,
  mapSecondSeniorRecipientToRegisterRequest,
  type SeniorCenterRegisterRequest,
} from '../pages/program/utils/senior-center-registration.mapper';

type StaffEnrollmentSessionResponse = {
  userId?: number;
  UserId?: number;
  token?: string;
  Token?: string;
};

@Injectable({
  providedIn: 'root',
})
export class SecondSeniorSubmitService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = (environment as { apiBaseUrl?: string }).apiBaseUrl || '';

  /**
   * Second-senior submit:
   * - Creates a dedicated users row for the recipient (mother) linked under the primary household.
   * - Applies user_program under the **primary** auth token so the application shows in the primary account.
   * - Adds a family_members row under the primary with linkedUserId → recipient.
   */
  submitSecondSeniorApplication(
    applyPayload: Record<string, unknown>,
    status: 'draft' | 'pending' | 're-submit'
  ): Observable<unknown> {
    const enrichedPayload = enrichSecondSeniorApplyPayload(applyPayload);
    const primaryUserId = this.resolvePrimaryUserId();
    const primaryToken = this.resolvePrimaryAuthToken();

    if (!primaryUserId || !primaryToken) {
      return throwError(() => new Error('Primary user session is required to apply for another senior'));
    }

    return this.createSecondSeniorUserSession(primaryUserId).pipe(
      switchMap(({ userId: motherUserId, token: motherToken }) => {
        const profileBody = mapSecondSeniorRecipientToRegisterRequest(enrichedPayload);
        return this.updateSecondSeniorProfile(profileBody, motherToken).pipe(
          switchMap(() => this.applyProgram(enrichedPayload, status, primaryToken)),
          switchMap((response) =>
            this.linkMotherAsFamilyMember(primaryUserId, motherUserId, profileBody, primaryToken).pipe(
              map(() => response),
              catchError(() => of(response))
            )
          ),
          map((response) => {
            if (typeof sessionStorage !== 'undefined') {
              try {
                sessionStorage.setItem('second_senior_participant_user_id', String(motherUserId));
              } catch {
                // ignore
              }
            }
            return response;
          })
        );
      })
    );
  }

  private resolvePrimaryUserId(): number | null {
    const raw =
      (typeof localStorage !== 'undefined' &&
        (localStorage.getItem('user_id') || localStorage.getItem('id'))) ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('user_id')) ||
      '';
    const fromStorage = Number(String(raw).trim());
    if (Number.isFinite(fromStorage) && fromStorage > 0) {
      return fromStorage;
    }

    const token = this.resolvePrimaryAuthToken();
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }
      const json = JSON.parse(
        atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))
      ) as Record<string, unknown>;
      const claim = Number(json['userId'] ?? json['UserId'] ?? json['sub'] ?? json['id']);
      return Number.isFinite(claim) && claim > 0 ? claim : null;
    } catch {
      return null;
    }
  }

  private resolvePrimaryAuthToken(): string {
    if (typeof sessionStorage !== 'undefined') {
      const sessionToken = (sessionStorage.getItem('auth_token') || '').trim();
      if (sessionToken) {
        return sessionToken;
      }
    }
    if (typeof localStorage !== 'undefined') {
      return (localStorage.getItem('auth_token') || '').trim();
    }
    return '';
  }

  private createSecondSeniorUserSession(
    householdPrimaryUserId: number
  ): Observable<{ userId: number; token: string }> {
    const body = {
      participantOrigin: 'second_senior',
      householdPrimaryUserId,
    };

    return this.http
      .post<StaffEnrollmentSessionResponse>(
        `${this.baseUrl}/api/auth/internal/staff-enrollment-session`,
        body
      )
      .pipe(
        map((response) => {
          const userId = Number(response?.userId ?? response?.UserId);
          const token = (response?.token ?? response?.Token ?? '').trim();
          if (!Number.isFinite(userId) || userId <= 0 || !token) {
            throw new Error('Failed to create second-senior participant user');
          }
          return { userId, token };
        })
      );
  }

  private updateSecondSeniorProfile(
    body: SeniorCenterRegisterRequest,
    token: string
  ): Observable<unknown> {
    return this.updateProfileWithBody(body, token).pipe(
      catchError((error) => {
        const status = error?.status ?? error?.error?.statusCode;
        const message = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
        const isIdentityConflict =
          status === 409 ||
          message.includes('already exists') ||
          message.includes('identification') ||
          message.includes('ssn');

        if (!isIdentityConflict) {
          return throwError(() => error);
        }

        const uniqueKey =
          globalThis.crypto?.randomUUID?.()?.replace(/-/g, '') ??
          `${Date.now()}${Math.floor(Math.random() * 1_000_000)}`;
        const fallbackEmail = `second-senior-${uniqueKey}@pending.local`;
        const fallbackPhone = `+1998${uniqueKey.replace(/\D/g, '').slice(0, 10).padEnd(10, '0')}`;
        const fallback: SeniorCenterRegisterRequest = {
          ...body,
          email: fallbackEmail,
          phone: fallbackPhone,
          identificationNumber: '',
          contacts: body.contacts.map((contact) => ({
            ...contact,
            email: fallbackEmail,
            ssn: '',
          })),
        };
        return this.updateProfileWithBody(fallback, token);
      })
    );
  }

  private updateProfileWithBody(
    body: SeniorCenterRegisterRequest,
    token: string
  ): Observable<unknown> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.put(`${this.baseUrl}/api/auth/profile`, body, { headers });
  }

  private applyProgram(
    applyPayload: Record<string, unknown>,
    status: 'draft' | 'pending' | 're-submit',
    primaryToken: string
  ): Observable<unknown> {
    const isDraft = status === 'draft';
    const payload = {
      ...applyPayload,
      status: isDraft ? 'draft' : status === 're-submit' ? 're-submit' : 'pending',
    };

    const headers = new HttpHeaders({
      Authorization: `Bearer ${primaryToken}`,
    });

    return this.http.post(`${this.baseUrl}/api/program/staticform/apply`, payload, { headers });
  }

  private linkMotherAsFamilyMember(
    primaryUserId: number,
    motherUserId: number,
    profile: SeniorCenterRegisterRequest,
    primaryToken: string
  ): Observable<unknown> {
    const primaryContact = profile.contacts?.[0];
    const body = {
      email: profile.email || undefined,
      firstName: primaryContact?.firstName || profile.name || 'Second Senior',
      lastName: primaryContact?.lastName || undefined,
      dob: primaryContact?.dob || undefined,
      gender: primaryContact?.gender || undefined,
      race: primaryContact?.race || undefined,
      ethnicity: primaryContact?.ethinicity || undefined,
      identificationType: profile.identificationType || undefined,
      identificationNumber: profile.identificationNumber || undefined,
      linkedUserId: motherUserId,
    };

    const headers = new HttpHeaders({
      Authorization: `Bearer ${primaryToken}`,
    });

    return this.http.post(
      `${this.baseUrl}/api/auth/internal/family-member/${primaryUserId}`,
      body,
      { headers }
    );
  }
}
